import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Clock, MapPin, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { services, PLANITY_URL } from '@/data/services';
import logoSrc from '@/assets/logo-body-by-cc.webp';

const navLinks = [
  { label: 'Accueil', path: '/' },
  { label: 'Soins', path: '/services', hasDropdown: true },
  { label: 'Formules', path: '/formules' },
  { label: 'Offrir', path: '/offrir' },
  { label: 'À propos', path: '/a-propos' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Blog', path: '/blog' },
  { label: 'Contact', path: '/contact' },
];

const SiteHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-petrol text-petrol-foreground">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-10 text-xs font-body">
          <div className="flex items-center gap-6">
            <a href="tel:+33645230034" className="flex items-center gap-2 hover:text-primary transition-colors">
              <Phone size={13} className="text-petrol-foreground/50" />
              06 45 23 00 34
            </a>
            <span className="hidden sm:flex items-center gap-2 text-petrol-foreground/60">
              <MapPin size={13} className="text-petrol-foreground/50" />
              65 Bd Westinghouse, 93270 Sevran
            </span>
          </div>
          <span className="flex items-center gap-2 text-petrol-foreground/60">
            <Clock size={13} className="text-petrol-foreground/50" />
            Mar-Dim : 10h-20h
          </span>
        </div>
      </div>

      <header className="fixed top-10 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center gap-3">
            <img src={logoSrc} alt="Body by CC" className="h-16 w-auto" width={64} height={64} />
            <div className="hidden sm:block">
              <span className="text-sm font-heading font-semibold text-foreground leading-tight block">Body by CC</span>
              <span className="text-[10px] text-muted-foreground leading-tight block font-body">Institut Santé & Esthétique</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div key={link.path} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setServicesOpen(!servicesOpen)}
                    className={`px-4 py-2 rounded-full text-sm font-medium font-body transition-colors inline-flex items-center gap-1 ${
                      location.pathname.startsWith('/services')
                        ? 'text-primary'
                        : 'text-foreground hover:text-primary'
                    }`}
                  >
                    {link.label}
                    <ChevronDown size={14} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {servicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-2 w-64 rounded-xl border border-border bg-card shadow-lg overflow-hidden"
                      >
                        <Link
                          to="/services"
                          onClick={() => { setServicesOpen(false); window.scrollTo(0, 0); }}
                          className="block px-4 py-3 text-sm font-medium font-body text-foreground hover:bg-muted/50 transition-colors border-b border-border"
                        >
                          Tous les soins
                        </Link>
                        {services.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            onClick={() => { setServicesOpen(false); window.scrollTo(0, 0); }}
                            className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground font-body hover:bg-muted/50 hover:text-foreground transition-colors"
                          >
                            <s.icon size={15} className="text-primary shrink-0" />
                            {s.title}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => window.scrollTo(0, 0)}
                  className={`px-4 py-2 rounded-full text-sm font-medium font-body transition-colors ${
                    location.pathname === link.path
                      ? 'text-primary'
                      : 'text-foreground hover:text-primary'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
            <a
              href={PLANITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 px-5 py-2 rounded-full text-sm font-medium font-body bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Prendre RDV
            </a>
          </nav>

          {/* Mobile actions */}
          <div className="lg:hidden flex items-center gap-0">
            <a
              href="tel:+33645230034"
              className="p-2 text-foreground hover:text-primary transition-colors"
              aria-label="Appeler"
            >
              <Phone size={22} />
            </a>
            <button
              onClick={() => setMobileOpen(true)}
              className="p-2 text-foreground"
              aria-label="Menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile fullscreen menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-[999] bg-card flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <Link to="/" onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }} className="flex items-center gap-3">
                <img src={logoSrc} alt="Body by CC" className="h-14 w-auto" width={56} height={56} />
                <span className="text-sm font-heading font-semibold text-foreground">Body by CC</span>
              </Link>
              <button onClick={() => setMobileOpen(false)} className="p-2 text-foreground" aria-label="Fermer">
                <X size={24} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                link.hasDropdown ? (
                  <div key={link.path}>
                    <button
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                      className={`w-full flex items-center justify-between py-3 px-4 rounded-lg text-lg font-body transition-colors ${
                        location.pathname.startsWith('/services')
                          ? 'bg-muted text-primary font-semibold'
                          : 'text-foreground/80 hover:bg-muted/50'
                      }`}
                    >
                      {link.label}
                      <ChevronDown size={18} className={`transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {mobileServicesOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <Link
                            to="/services"
                            onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }}
                            className="block py-2 px-8 text-base font-body text-muted-foreground hover:text-primary transition-colors"
                          >
                            Tous les soins
                          </Link>
                          {services.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/services/${s.slug}`}
                              onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }}
                              className="flex items-center gap-2 py-2 px-8 text-base font-body text-muted-foreground hover:text-primary transition-colors"
                            >
                              <s.icon size={14} className="text-primary shrink-0" />
                              {s.title}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => { setMobileOpen(false); window.scrollTo(0, 0); }}
                    className={`py-3 px-4 rounded-lg text-lg font-body transition-colors ${
                      location.pathname === link.path
                        ? 'bg-muted text-primary font-semibold'
                        : 'text-foreground/80 hover:bg-muted/50'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </nav>

            <div className="border-t border-border px-6 py-6 flex flex-col gap-3">
              <a
                href="tel:+33645230034"
                className="w-full py-3 rounded-full border border-border text-center font-medium font-body text-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
              >
                <Phone size={16} />
                06 45 23 00 34
              </a>
              <a
                href={PLANITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 rounded-full bg-primary text-primary-foreground text-center font-medium font-body hover:bg-primary/90 transition-colors"
              >
                Prendre RDV
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SiteHeader;